export interface InventoryTransferSuggestionItemAPIInterface {
  product_id: string;
  variant_id: string;
  inventory_item_id: string;
  product: string;
  variant: string;
  sku: string;
  barcode: string;
  image: string;
  suggested_quantity: string;
  pending_transfer_quantity?: number;
  original_suggested_quantity?: number;
  has_pending_transfer?: boolean;
  destination_strategy: string;
  destination_safety_stock_days: string;
  destination_min_stock_days: string;
  destination_max_stock_days: string;
  destination_target_stock_days: string;
  destination_location: string;
  destination_location_id: string;
  source_strategy: string;
  source_safety_stock_days: string;
  source_min_stock_days: string;
  source_max_stock_days: string;
  source_target_stock_days: string;
  source_location: string;
  source_location_id: string;
  reorder_point: string;
  transferable_quantity: string;
  lead_time_days: string;
  source_inventory: string;
  destination_inventory: string;
  source_avg_daily_sales: string;
  destination_avg_daily_sales: string;
  destination_need: string;
  destination_min_quantity?: string;
  destination_max_quantity?: string;
  // Priority scores from OLAP
  priority_score?: string;
  stockout_risk_score?: string;
  revenue_impact_score?: string;
  lead_time_urgency_score?: string;
  // New fields from Story 12
  vendor?: string;
  unit_price?: string;
  total_price?: string;
}

export interface InventoryTransferSuggestionItemInterface {
  productId: number;
  variantId: number;
  inventoryItemId: number;
  productTitle: string;
  variantTitle: string;
  sku: string;
  barcode: string;
  imageUrl: string;
  suggestedQuantity: number;
  pendingTransferQuantity?: number;
  originalSuggestedQuantity?: number;
  hasPendingTransfer?: boolean;
  destinationStrategy: string;
  destinationSafetyStockDays: number;
  destinationMinStockDays: number;
  destinationMaxStockDays: number;
  destinationTargetStockDays: number;
  destinationLocation: string;
  destinationLocationId: number;
  sourceStrategy: string;
  sourceSafetyStockDays: number;
  sourceMinStockDays: number;
  sourceMaxStockDays: number;
  sourceTargetStockDays: number;
  sourceLocation: string;
  sourceLocationId: number;
  reorderPoint: number;
  transferableQuantity: number;
  leadTimeDays: number;
  sourceInventory: number;
  destinationInventory: number;
  sourceAvgDailySales: number;
  destinationAvgDailySales: number;
  destinationNeed: number;
  destinationMinQuantity?: number;
  destinationMaxQuantity?: number;
  // Priority scores from OLAP
  priorityScore: number;
  stockoutRiskScore: number;
  revenueImpactScore: number;
  leadTimeUrgencyScore: number;
  // New fields from Story 12
  vendor?: string;
  unitPrice?: number;
  totalPrice?: number;
}

export interface InventoryTransferSuggestionSummaryAPIInterface {
  total_suggested_quantity: number;
  count_variants: number;
  total_pending_quantity?: number;
}

export interface InventoryTransferSuggestionSummaryInterface {
  totalSuggestedQuantity: number;
  countVariants: number;
  totalPendingQuantity?: number;
}

export interface InventoryTransferSuggestionAPIInterface {
  from_location_id: number | string;
  from_location_name: string;
  to_location_id: number | string;
  to_location_name: string;
  suggestions: InventoryTransferSuggestionItemAPIInterface[];
  summary: InventoryTransferSuggestionSummaryAPIInterface;
}

export class InventoryTransferSuggestionModel {
  public fromLocationId: number;
  public fromLocationName: string;
  public toLocationId: number;
  public toLocationName: string;
  public suggestions: InventoryTransferSuggestionItemInterface[];
  public summary: InventoryTransferSuggestionSummaryInterface;

  constructor(data?: Partial<InventoryTransferSuggestionModel>) {
    this.fromLocationId = data?.fromLocationId ?? 0;
    this.fromLocationName = data?.fromLocationName ?? '';
    this.toLocationId = data?.toLocationId ?? 0;
    this.toLocationName = data?.toLocationName ?? '';
    this.suggestions = data?.suggestions ?? [];
    this.summary = data?.summary ?? { totalSuggestedQuantity: 0, countVariants: 0 };
  }

  public mapData(data: InventoryTransferSuggestionAPIInterface): this {
    this.fromLocationId = Number(data.from_location_id);
    this.fromLocationName = data.from_location_name;
    this.toLocationId = Number(data.to_location_id);
    this.toLocationName = data.to_location_name;
    this.suggestions = data.suggestions.map((item) => {
      return {
        productId: Number(item.product_id) || 0,
        variantId: Number(item.variant_id) || 0,
        inventoryItemId: Number(item.inventory_item_id) || 0,
        productTitle: item.product ?? '',
        variantTitle: item.variant ?? '',
        sku: item.sku ?? '',
        barcode: item.barcode ?? '',
        imageUrl: item.image ?? '',
        suggestedQuantity: Number(item.suggested_quantity) || 0,
        pendingTransferQuantity: item.pending_transfer_quantity || 0,
        originalSuggestedQuantity: item.original_suggested_quantity || 0,
        hasPendingTransfer: item.has_pending_transfer || false,
        destinationStrategy: item.destination_strategy ?? '',
        destinationSafetyStockDays: Number(item.destination_safety_stock_days) || 0,
        destinationMinStockDays: Number(item.destination_min_stock_days) || 0,
        destinationMaxStockDays: Number(item.destination_max_stock_days) || 0,
        destinationTargetStockDays: Number(item.destination_target_stock_days) || 0,
        destinationLocation: item.destination_location ?? '',
        destinationLocationId: Number(item.destination_location_id) || 0,
        sourceStrategy: item.source_strategy ?? '',
        sourceSafetyStockDays: Number(item.source_safety_stock_days) || 0,
        sourceMinStockDays: Number(item.source_min_stock_days) || 0,
        sourceMaxStockDays: Number(item.source_max_stock_days) || 0,
        sourceTargetStockDays: Number(item.source_target_stock_days) || 0,
        sourceLocation: item.source_location ?? '',
        sourceLocationId: Number(item.source_location_id) || 0,
        reorderPoint: Number(item.reorder_point) || 0,
        transferableQuantity: Number(item.transferable_quantity) || 0,
        leadTimeDays: Number(item.lead_time_days) || 0,
        sourceInventory: Number(item.source_inventory) || 0,
        destinationInventory: Number(item.destination_inventory) || 0,
        sourceAvgDailySales: Number(item.source_avg_daily_sales) || 0,
        destinationAvgDailySales: Number(item.destination_avg_daily_sales) || 0,
        destinationNeed: Number(item.destination_need) || 0,
        destinationMinQuantity: item.destination_min_quantity ? Number(item.destination_min_quantity) : undefined,
        destinationMaxQuantity: item.destination_max_quantity ? Number(item.destination_max_quantity) : undefined,
        // Priority scores from OLAP
        priorityScore: Number(item.priority_score) || 0,
        stockoutRiskScore: Number(item.stockout_risk_score) || 0,
        revenueImpactScore: Number(item.revenue_impact_score) || 0,
        leadTimeUrgencyScore: Number(item.lead_time_urgency_score) || 0,
        // New fields from Story 12
        vendor: item.vendor ?? '',
        unitPrice: item.unit_price ? Number(item.unit_price) : undefined,
        totalPrice: item.total_price ? Number(item.total_price) : undefined,
      };
    });
    this.summary = {
      totalSuggestedQuantity: data.summary.total_suggested_quantity ?? 0,
      countVariants: data.summary.count_variants ?? 0,
      totalPendingQuantity: data.summary.total_pending_quantity ?? 0,
    };
    return this;
  }
}
